// Chords you can hear: a strum of the shape the diagram shows, on the
// player's instrument, and a drone on the song's key to find the starting
// note by. Strums use the tuner's recorded guitar (or bass) notes; until
// they've loaded, or if they can't be, the strings are synthesised
// (Karplus–Strong).

import { getAudioContext, isAudioSupported, resumeAudio } from './audio';
import { parseNote } from '../tuner/pitch';
import { loadReferenceNote, playbackRate } from '../tuner/samples';

const STRUM_GAP = 0.022; // seconds between strings in a strum
const RING = 2.6; // seconds a strummed note is generated for
// How long a tap waits for recordings that haven't loaded yet.
const SAMPLE_WAIT_MS = 300;

const midiHz = midi => 440 * Math.pow(2, (midi - 69) / 12);

// Frets of a chord shape from the site's chord catalogue: one character per
// string, low string first; 'x' is muted, 'o' open, digits are frets counted
// from the diagram's first fret (`fret`, 1 at the nut).
export function shapeFrets(chord) {
    if (!chord || !chord.fingering) return null;
    const base = chord.fret > 1 ? chord.fret - 1 : 0;
    const frets = chord.fingering.split('').map(c => {
        if (c === 'x' || c === 'X') return null;
        if (c === 'o' || c === 'O' || c === '0') return 0;
        const n = parseInt(c, 36);
        return Number.isNaN(n) ? null : n + base;
    });
    return frets.some(f => f !== null) ? frets : null;
}

// The notes the chord generator wrote into a diagram, low string first
// ("C3:E3:G3:C4:E4"), when it says. It works them out on a guitar in
// standard tuning whatever the instrument, and counts fretted notes from the
// nut whatever the diagram's first fret, so the fretted ones are moved up to
// that fret here; open strings it gets right.
function diagramNotes(chord) {
    const match = chord && chord.svg_code && /<notes id="tonal-notes">([^<]*)<\/notes>/.exec(chord.svg_code);
    if (!match) return null;
    const notes = match[1].split(':').map(note => parseNote(note.trim()));
    if (!notes.length || notes.some(n => n === null)) return null;
    const base = chord.fret > 1 ? chord.fret - 1 : 0;
    if (!base) return notes;
    const played = (chord.fingering || '').split('').filter(c => c !== 'x' && c !== 'X');
    if (played.length !== notes.length) return null;
    return notes.map((midi, i) => (/^[oO0]$/.test(played[i]) ? midi : midi + base));
}

// MIDI notes to strum, low to high: the diagram the player sees (its shape on
// `tuning`, the open strings as MIDI notes, or the notes the diagram was
// drawn for) when there is one, otherwise the chord spread out the way a
// guitarist might play it.
export function chordNotes(parsed, chord, tuning) {
    const frets = shapeFrets(chord);
    if (frets && tuning && frets.length === tuning.length) {
        return frets.map((fret, i) => (fret === null ? null : tuning[i] + fret)).filter(n => n !== null);
    }
    const drawn = diagramNotes(chord);
    if (drawn) return drawn;
    if (!parsed) return [];
    const bass = 40 + ((parsed.bass - 40) % 12 + 12) % 12; // E2–D#3
    const notes = [bass];
    let last = bass;
    // Two rounds of the chord tones above the bass: a full, open voicing.
    for (let round = 0; round < 2; round++) {
        parsed.pcs.forEach(pc => {
            let midi = last - (last % 12) + pc;
            while (midi <= last + 2) midi += 12;
            if (midi <= 76 && notes.length < 6) {
                notes.push(midi);
                last = midi;
            }
        });
    }
    return notes.sort((a, b) => a - b);
}

// One plucked note, as a buffer: a burst of noise circulating through a
// delay line of one period with a gentle low-pass, which is how a string's
// overtones die away faster than its fundamental.
function pluck(ctx, frequency) {
    const rate = ctx.sampleRate;
    const period = rate / frequency;
    const length = Math.floor(rate * RING);
    const buffer = ctx.createBuffer(1, length, rate);
    const out = buffer.getChannelData(0);
    const size = Math.max(2, Math.floor(period - 0.5));
    // A first-order all-pass makes up the fraction of a sample the integer
    // delay line can't, so the note is in tune.
    const fraction = period - 0.5 - size;
    const allpass = (1 - fraction) / (1 + fraction);
    const ring = new Float32Array(size);
    let smooth = 0;
    for (let i = 0; i < size; i++) {
        // Softened noise: a pick rather than a fingernail.
        smooth += 0.55 * (Math.random() * 2 - 1 - smooth);
        ring[i] = smooth;
    }
    // Low strings ring longer than high ones.
    const seconds = Math.max(1.2, Math.min(4, 5.5 - frequency / 150));
    const loss = Math.pow(0.001, 1 / (frequency * seconds));
    let index = 0;
    let previousIn = 0;
    let previousOut = 0;
    for (let i = 0; i < length; i++) {
        const current = ring[index];
        const next = ring[(index + 1) % size];
        out[i] = current;
        const averaged = loss * 0.5 * (current + next);
        const tuned = allpass * averaged + previousIn - allpass * previousOut;
        previousIn = averaged;
        previousOut = tuned;
        ring[index] = tuned;
        index = (index + 1) % size;
    }
    return buffer;
}

// Notes and keys are given as written (see chordNotes); with a capo on (see
// setCapo) they sound that many semitones higher, as on the player's guitar.
export default class ChordPlayer {
    constructor() {
        this.buffers = new Map();
        this.samples = new Map();
        this.sound = 'guitar';
        this.capo = 0;
        this.ringing = [];
        this.drone = null;
    }

    // 'guitar' or 'bass' (see tuner/samples).
    setSound(sound) {
        this.sound = sound;
    }

    // The fret the player's capo is on, 0 for none. A drone that's on moves
    // with it.
    setCapo(capo) {
        if (capo === this.capo) return;
        this.capo = capo;
        if (this.drone) this.startDrone(this.drone.pc);
    }

    sounding(notes) {
        return notes.map(midi => midi + this.capo);
    }

    // Fetch the recordings for these notes ahead of the first tap.
    preload(notes) {
        return Promise.all(this.sounding(notes).map(midi => {
            const key = `${this.sound}:${midi}`;
            if (this.samples.has(key)) return null;
            return loadReferenceNote(this.sound, midi)
                .then(sample => this.samples.set(key, sample))
                .catch(() => {});
        }));
    }

    output() {
        const ctx = getAudioContext();
        if (!this.bus || this.bus.context !== ctx) {
            this.buffers.clear();
            this.bus = ctx.createGain();
            this.bus.gain.value = 0.5;
            // A little body: less fizz, a bit more wood.
            const body = ctx.createBiquadFilter();
            body.type = 'lowpass';
            body.frequency.value = 3800;
            this.bus.connect(body).connect(ctx.destination);
        }
        return this.bus;
    }

    note(midi) {
        if (!this.buffers.has(midi)) {
            this.buffers.set(midi, pluck(getAudioContext(), midiHz(midi)));
        }
        return this.buffers.get(midi);
    }

    // Strum `written` (MIDI, low to high) downwards, damping the last strum.
    async strum(written) {
        if (!written.length || !isAudioSupported()) return;
        const notes = this.sounding(written);
        // Resume inside the tap: browsers only let audio start from one.
        const ctx = resumeAudio();
        const tap = (this.tap || 0) + 1;
        this.tap = tap;
        await Promise.race([this.preload(written), new Promise(resolve => setTimeout(resolve, SAMPLE_WAIT_MS))]);
        if (this.tap !== tap) return; // another chord was tapped meanwhile
        const output = this.output();
        const now = ctx.currentTime;
        this.ringing.forEach(gain => {
            gain.gain.cancelScheduledValues(now);
            gain.gain.setTargetAtTime(0.0001, now, 0.04);
        });
        const start = now + 0.03;
        this.ringing = notes.map((midi, i) => {
            const source = ctx.createBufferSource();
            const sample = this.samples.get(`${this.sound}:${midi}`);
            const gain = ctx.createGain();
            if (sample) {
                source.buffer = sample.buffer;
                source.playbackRate.value = playbackRate(sample, midi, 440);
                gain.gain.value = 0.9 - 0.05 * i;
                source.start(start + i * STRUM_GAP, sample.offset);
            } else {
                source.buffer = this.note(midi);
                gain.gain.value = 0.55 - 0.03 * i;
                source.start(start + i * STRUM_GAP);
            }
            source.connect(gain).connect(output);
            source.stop(start + i * STRUM_GAP + 6);
            return gain;
        });
    }

    // A soft, sustained root and fifth on `pc` (0 = C, as written) until
    // stopDrone().
    startDrone(pc) {
        this.stopDrone();
        if (!isAudioSupported()) return;
        const ctx = resumeAudio();
        const output = this.output();
        const now = ctx.currentTime;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.35, now + 0.8);
        const filter = ctx.createBiquadFilter();
        filter.type = 'lowpass';
        filter.frequency.value = 900;
        filter.connect(gain).connect(output);
        const root = 48 + (pc + this.capo) % 12; // C3–B3
        const oscillators = [root - 12, root, root + 7].flatMap((midi, i) => [-4, 4].map(detune => {
            const osc = ctx.createOscillator();
            osc.type = i === 0 ? 'triangle' : 'sawtooth';
            osc.frequency.value = midiHz(midi);
            osc.detune.value = detune;
            const level = ctx.createGain();
            level.gain.value = i === 0 ? 0.5 : 0.18;
            osc.connect(level).connect(filter);
            osc.start(now);
            return osc;
        }));
        this.drone = { gain, oscillators, pc };
    }

    stopDrone() {
        if (!this.drone) return;
        const ctx = getAudioContext();
        const now = ctx.currentTime;
        const { gain, oscillators } = this.drone;
        gain.gain.cancelScheduledValues(now);
        gain.gain.setTargetAtTime(0.0001, now, 0.15);
        oscillators.forEach(osc => osc.stop(now + 1));
        this.drone = null;
    }

    // Short cues for the chord-change trainer: 'tick' while counting in,
    // 'go', and 'end'.
    beep(kind) {
        if (!isAudioSupported()) return;
        const ctx = resumeAudio();
        const now = ctx.currentTime;
        const osc = ctx.createOscillator();
        osc.frequency.value = { tick: 660, go: 1320, end: 523 }[kind] || 880;
        const gain = ctx.createGain();
        gain.gain.setValueAtTime(0.0001, now);
        gain.gain.exponentialRampToValueAtTime(0.3, now + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + (kind === 'end' ? 0.6 : kind === 'go' ? 0.3 : 0.1));
        osc.connect(gain).connect(ctx.destination);
        osc.start(now);
        osc.stop(now + 0.7);
    }
}
